import React, { useRef, useEffect } from 'react';
import { UserMessage } from './UserMessage';
import { AIMessage } from './AIMessage';
import { SystemMessage } from './SystemMessage';
import type { ChatMessage as ChatMessageType } from '../../services/chatService';

interface ChatMessagesProps {
  messages: ChatMessageType[];
  userInitials: string;
  isLoading?: boolean;
  onApproveAction?: (messageId: string) => Promise<void>;
  onRejectAction?: (messageId: string) => Promise<void>;
  onCreateRequirement?: (requirementData: { title: string; description: string; requirement_type: string; priority: string }) => void;
}

export function ChatMessages({
  messages,
  userInitials,
  isLoading = false,
  onApproveAction,
  onRejectAction,
  onCreateRequirement
}: ChatMessagesProps): React.ReactElement {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50"
      style={{ minHeight: '300px', maxHeight: 'calc(100vh - 400px)' }}
    >
      {messages.length === 0 && !isLoading ? (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="text-sm">Welcome to the Requirements Assistant!</p>
          <p className="text-xs mt-2 text-center max-w-sm">
            Ask me questions about your requirements, or ask me to help you create new ones.
          </p>
        </div>
      ) : (
        <>
          {messages.map((msg) => {
            const timestamp = new Date(msg.created_at);

            if (msg.message_type === 'user') {
              return (
                <UserMessage
                  key={msg.id}
                  content={msg.message_content}
                  timestamp={timestamp}
                  userInitials={userInitials}
                />
              );
            } else if (msg.message_type === 'ai') {
              return (
                <AIMessage
                  key={msg.id}
                  messageId={msg.id}
                  content={msg.message_content}
                  timestamp={timestamp}
                  actionType={msg.action_type}
                  actionStatus={msg.action_status}
                  actionData={msg.action_data}
                  onApproveAction={onApproveAction}
                  onRejectAction={onRejectAction}
                  onCreateRequirement={onCreateRequirement}
                />
              );
            } else if (msg.message_type === 'system') {
              // Determine system message type based on content
              let systemType: 'success' | 'error' | 'info' | 'warning' = 'info';
              if (msg.message_content.toLowerCase().includes('error') || msg.message_content.toLowerCase().includes('failed')) {
                systemType = 'error';
              } else if (msg.message_content.toLowerCase().includes('success') || msg.message_content.includes('✓')) {
                systemType = 'success';
              } else if (msg.message_content.toLowerCase().includes('warning') || msg.message_content.includes('⚠')) {
                systemType = 'warning';
              }

              return (
                <SystemMessage
                  key={msg.id}
                  content={msg.message_content}
                  type={systemType}
                />
              );
            }

            return null;
          })}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex items-start gap-2 mb-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 border border-purple-200 flex items-center justify-center shadow-sm">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div className="flex flex-col max-w-[80%]">
                <div className="bg-gray-100 border border-gray-200 text-gray-900 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                    <span className="text-xs text-gray-500">AI is thinking...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </>
      )}
    </div>
  );
}

export default ChatMessages;
